// src/lib/kinema/core/handlers/effectHandlers.ts
import type { EditorState, InverseOperation, Keyframe } from '../types';
import {
  ADD_EFFECT, REMOVE_EFFECT, ADD_KEYFRAME, REMOVE_KEYFRAME,
  UPDATE_KEYFRAME, TOGGLE_EFFECT,
} from '../commands/commands';
import { cloneState } from '../EditorState';

let effectIdCounter = 0;

function cloneLayerEffects(state: EditorState, layerId: string): EditorState {
  const newState = cloneState(state);
  const layer = newState.layers[layerId];
  if (!layer) return newState;
  newState.layers[layerId] = {
    ...layer,
    effects: { ...layer.effects },
  };
  return newState;
}

export function handleAddEffect(
  state: EditorState,
  payload: { layerId: string; property: string; keyframes: Keyframe<any>[]; id?: string; enabled?: boolean },
): { state: EditorState; inverse: InverseOperation | null } {
  if (!state.layers[payload.layerId]) return { state, inverse: null };

  const newState = cloneLayerEffects(state, payload.layerId);
  const effectId = payload.id ?? `eff-${++effectIdCounter}`;

  newState.layers[payload.layerId].effects[payload.property] = {
    id: effectId,
    property: payload.property,
    keyframes: [...payload.keyframes].sort((a, b) => a.time - b.time),
    enabled: payload.enabled ?? true,
  };

  return {
    state: newState,
    inverse: { command: REMOVE_EFFECT, payload: { layerId: payload.layerId, property: payload.property } },
  };
}

export function handleAddKeyframe(
  state: EditorState,
  payload: { layerId: string; property: string; keyframe: Keyframe<any> },
): { state: EditorState; inverse: InverseOperation | null } {
  const layer = state.layers[payload.layerId];
  const effect = layer?.effects[payload.property];
  if (!layer || !effect) return { state, inverse: null };

  const newState = cloneLayerEffects(state, payload.layerId);
  const newKeyframes = [...effect.keyframes, payload.keyframe].sort((a, b) => a.time - b.time);

  newState.layers[payload.layerId].effects[payload.property] = {
    ...effect,
    keyframes: newKeyframes,
  };

  return {
    state: newState,
    inverse: {
      command: REMOVE_KEYFRAME,
      payload: { layerId: payload.layerId, property: payload.property, time: payload.keyframe.time },
    },
  };
}

export function handleUpdateKeyframe(
  state: EditorState,
  payload: { layerId: string; property: string; time: number; value?: any; easing?: any },
): { state: EditorState; inverse: InverseOperation | null } {
  const layer = state.layers[payload.layerId];
  const effect = layer?.effects[payload.property];
  if (!layer || !effect) return { state, inverse: null };

  const kfIndex = effect.keyframes.findIndex((kf) => kf.time === payload.time);
  if (kfIndex === -1) return { state, inverse: null };

  const oldKf = effect.keyframes[kfIndex];
  const newKf = {
    ...oldKf,
    ...(payload.value !== undefined && { value: payload.value }),
    ...(payload.easing !== undefined && { easing: payload.easing }),
  };

  const newState = cloneLayerEffects(state, payload.layerId);
  const newKeyframes = [...effect.keyframes];
  newKeyframes[kfIndex] = newKf;
  newState.layers[payload.layerId].effects[payload.property] = {
    ...effect,
    keyframes: newKeyframes,
  };

  return {
    state: newState,
    inverse: {
      command: UPDATE_KEYFRAME,
      payload: { layerId: payload.layerId, property: payload.property, time: payload.time, value: oldKf.value, easing: oldKf.easing },
    },
  };
}

export function handleRemoveKeyframe(
  state: EditorState,
  payload: { layerId: string; property: string; time: number },
): { state: EditorState; inverse: InverseOperation | null } {
  const layer = state.layers[payload.layerId];
  const effect = layer?.effects[payload.property];
  if (!layer || !effect) return { state, inverse: null };

  const removedKf = effect.keyframes.find((kf) => kf.time === payload.time);
  if (!removedKf) return { state, inverse: null };

  const newState = cloneLayerEffects(state, payload.layerId);
  newState.layers[payload.layerId].effects[payload.property] = {
    ...effect,
    keyframes: effect.keyframes.filter((kf) => kf.time !== payload.time),
  };

  return {
    state: newState,
    inverse: {
      command: ADD_KEYFRAME,
      payload: { layerId: payload.layerId, property: payload.property, keyframe: removedKf },
    },
  };
}

export function handleRemoveEffect(
  state: EditorState,
  payload: { layerId: string; property: string },
): { state: EditorState; inverse: InverseOperation | null } {
  const layer = state.layers[payload.layerId];
  const effect = layer?.effects[payload.property];
  if (!layer || !effect) return { state, inverse: null };

  const snapshot = { ...effect, keyframes: [...effect.keyframes] };
  const newState = cloneLayerEffects(state, payload.layerId);
  delete newState.layers[payload.layerId].effects[payload.property];

  return {
    state: newState,
    inverse: {
      command: ADD_EFFECT,
      payload: {
        layerId: payload.layerId,
        property: payload.property,
        keyframes: snapshot.keyframes,
        id: snapshot.id,           // preserve original effect ID on undo
        enabled: snapshot.enabled, // preserve enabled state on undo
      },
    },
  };
}

export function handleToggleEffect(
  state: EditorState,
  payload: { layerId: string; property: string; enabled: boolean },
): { state: EditorState; inverse: InverseOperation | null } {
  const layer = state.layers[payload.layerId];
  const effect = layer?.effects[payload.property];
  if (!layer || !effect) return { state, inverse: null };

  const oldEnabled = effect.enabled;
  const newState = cloneLayerEffects(state, payload.layerId);
  newState.layers[payload.layerId].effects[payload.property] = {
    ...effect,
    enabled: payload.enabled,
  };

  return {
    state: newState,
    inverse: {
      command: TOGGLE_EFFECT,
      payload: { layerId: payload.layerId, property: payload.property, enabled: oldEnabled },
    },
  };
}
